export type Workspace = { code: string; label: string };

function formatWorkspaceLabel(raw: string): string {
  return raw
    .split(/([\s\-_/]+)/)
    .map((part) => {
      if (!part || /[\s\-_/]+/.test(part)) return part;

      // Keep short technical tokens/acronyms uppercase (A6, QTL, FO, S1, etc.)
      if (part.length <= 3 || /\d/.test(part)) {
        return part.toUpperCase();
      }

      // If already all-caps, keep as-is
      if (part === part.toUpperCase()) {
        return part;
      }

      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join("");
}

export function normalizeCode(s?: string | null): string {
  if (!s) return "";
  return s
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type UserShape = {
  workbooks?: Record<
    string,
    Record<
      string,
      Record<
        string,
        Record<
          string,
          { label?: string | null; workspace_group?: string | null } | string
        >
      >
    >
  >;
  client_labels?: Record<string, string>;
  tables?: Record<
    string,
    Record<
      string,
      Record<
        string,
        { label?: string | null; workspace_group?: string | null } | string
      >
    >
  >;
};

export function getWorkspaceCodeFromUrl(
  pathname: string,
  workbooksByMenu: Record<
    string,
    Record<
      string,
      Record<
        string,
        Record<
          string,
          { label?: string | null; workspace_group?: string | null } | string
        >
      >
    >
  > = {},
) {
  const parts = pathname.split("/").filter(Boolean);
  const section = parts[0]; // "admin" | "dashboard" | ...
  const sub = parts[1]; // client code OR dashboard uuid
  if (!section || !sub) return null;

  if (section === "w") {
    const workspace = parts[2];
    return workspace ? decodeURIComponent(workspace) : null;
  }

  if (section === "admin") {
    try {
      return decodeURIComponent(sub);
    } catch {
      return sub;
    }
  }
  if (section === "dashboard") {
    for (const workspaces of Object.values(workbooksByMenu)) {
      for (const [workspaceName, menus] of Object.entries(workspaces)) {
        for (const entries of Object.values(menus)) {
          if (sub in entries) return workspaceName;
        }
      }
    }
    return null;
  }
  return null;
}

// already in your file; kept here for completeness
export function deriveWorkspacesFromWorkbooks(user?: UserShape): Workspace[] {
  if (!user?.workbooks) return [];
  const labelsByCode = new Map<string, string>();
  for (const byWorkspace of Object.values(user.workbooks)) {
    for (const workspace of Object.keys(byWorkspace)) {
      const code = normalizeCode(workspace);
      if (!code) continue;
      if (!labelsByCode.has(code))
        labelsByCode.set(code, formatWorkspaceLabel(workspace));
    }
  }
  return Array.from(labelsByCode.entries())
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build workspaces from user.tables top-level keys.
 * label = exactly the key shown (e.g. "A6", "QTL - Referential")
 * code  = slug (e.g. "a6", "qtl-referential")
 */
export function deriveWorkspacesFromTables(user?: UserShape): Workspace[] {
  if (!user?.tables) return [];
  const labelsByCode = new Map<string, string>();
  for (const byMenu of Object.values(user.tables)) {
    for (const menu of Object.keys(byMenu)) {
      const code = normalizeCode(menu);
      if (!code) continue;
      if (!labelsByCode.has(code))
        labelsByCode.set(code, formatWorkspaceLabel(menu));
    }
  }
  return Array.from(labelsByCode.entries())
    .map(([code, label]) => ({ code, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Merge/dedup two workspace lists (by code), prefer the first list’s label on conflicts */
export function mergeWorkspaceLists(
  a: Workspace[],
  b: Workspace[],
): Workspace[] {
  const seen = new Set<string>();
  const out: Workspace[] = [];
  for (const w of [...a, ...b]) {
    if (w.code && !seen.has(w.code)) {
      seen.add(w.code);
      out.push(w);
    }
  }
  return out;
}
